<template>
  <div class="tj-login">
    <div class="bg">
      <canvas ref="particleCanvas" class="bg-canvas" />
      <div class="bg-dots" />
    </div>

    <div class="container">
      <div v-if="!isMobile" class="left">
        <div class="title-reveal">
          <img
            v-if="loginImg"
            :src="loginImg"
            alt="联帮帮Logo"
            class="logo-img"
          />
          <div v-else class="logo-fallback brand-text-gradient">联帮帮</div>
        </div>

        <div class="slogan">
          <div class="title-reveal delay-100">
            <div class="slogan-cn slogan-cn-1">智慧联动</div>
            <div class="slogan-cn slogan-cn-2 brand-text-gradient">
              帮运未来
            </div>
          </div>

          <div class="title-reveal delay-200 slogan-en">
            <div>Smart Connection</div>
            <div class="brand-text-gradient">Future Delivery</div>
          </div>

          <div class="title-reveal delay-300 deco">
            <div class="deco-bar" />
            <div class="deco-dot" />
            <div class="deco-line" />
          </div>
        </div>

        <div class="title-reveal delay-300 value">
          <div>AI-Driven Digital Logistics Ecosystem</div>
          <div class="value-sub">构建智能、高效、可信的全球运力网络</div>
        </div>
      </div>

      <div class="right">
        <div class="card glass-card">
          <div v-if="isMobile" class="mobile-brand">
            <img
              v-if="loginImg"
              :src="loginImg"
              alt="联帮帮Logo"
              class="logo-img-mobile"
            />
            <div v-else class="logo-fallback brand-text-gradient">联帮帮</div>
            <div class="mobile-sub">智慧联动，帮运未来</div>
          </div>

          <div class="header">
            <div class="h-title">{{ headerTitle }}</div>
            <div class="h-sub">{{ headerSub }}</div>
          </div>

          <div v-if="viewMode === 'login'">
            <div class="tabs">
              <button
                class="tab-btn"
                :class="{ active: activeTab === 'sms' }"
                type="button"
                @click="switchTab('sms')"
              >
                短信登录
              </button>
              <button
                class="tab-btn"
                :class="{ active: activeTab === 'password' }"
                type="button"
                @click="switchTab('password')"
              >
                密码登录
              </button>
            </div>

            <form
              v-if="activeTab === 'sms'"
              class="form"
              @submit.prevent="submitSms"
            >
              <div class="field">
                <div class="label">手机号码</div>
                <div class="input-wrap">
                  <input
                    v-model.trim="smsForm.phone"
                    class="input input-elegant"
                    type="tel"
                    inputmode="numeric"
                    maxlength="11"
                    placeholder="请输入11位手机号"
                    @input="onPhoneInput"
                  />
                  <div class="input-icon">
                    <svg class="icon" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                  </div>
                </div>
              </div>

              <div ref="slider" class="slider">
                <div class="slider-text" :class="{ verified: sliderVerified }">
                  {{ sliderText }}
                </div>
                <div
                  class="slider-progress"
                  :class="{ verified: sliderVerified }"
                  :style="sliderProgressStyle"
                />
                <button
                  class="slider-thumb slider-thumb-btn"
                  :class="{
                    verified: sliderVerified,
                    dragging: sliderDragging
                  }"
                  type="button"
                  :style="sliderThumbStyle"
                  @mousedown.prevent="onThumbDown"
                  @touchstart.prevent="onThumbDown"
                >
                  <svg
                    v-if="!sliderVerified"
                    class="icon"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <path
                      d="M9 5l7 7-7 7"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                  <svg
                    v-else
                    class="icon verified"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <path
                      d="M5 13l4 4L19 7"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </button>
              </div>

              <div class="field">
                <div class="label">验证码</div>
                <div class="code-row">
                  <input
                    v-model.trim="smsForm.verifyCode"
                    class="input input-elegant"
                    type="text"
                    inputmode="numeric"
                    maxlength="6"
                    placeholder="请输入6位验证码"
                  />
                  <button
                    class="code-btn"
                    type="button"
                    :disabled="codeSendingDisabled"
                    @click="sendCode"
                  >
                    {{ codeBtnText }}
                  </button>
                </div>
              </div>

              <button
                class="btn btn-primary"
                type="submit"
                :disabled="smsSubmitting"
              >
                <span v-if="!smsSubmitting">登 录</span>
                <span v-else class="loading">
                  <svg class="spinner" viewBox="0 0 24 24">
                    <circle class="spinner-track" cx="12" cy="12" r="10" />
                    <path class="spinner-head" d="M4 12a8 8 0 018-8" />
                  </svg>
                </span>
              </button>
            </form>

            <form v-else class="form" @submit.prevent="submitPassword">
              <div class="field">
                <div class="label">账号</div>
                <input
                  v-model.trim="pwdForm.username"
                  class="input input-elegant"
                  type="text"
                  maxlength="11"
                  placeholder="请输入账号"
                />
              </div>

              <div class="field">
                <div class="label">密码</div>
                <div class="input-wrap">
                  <input
                    v-model="pwdForm.userPwd"
                    class="input input-elegant"
                    :type="passwordVisible ? 'text' : 'password'"
                    placeholder="请输入密码"
                    @keyup.enter="submitPassword"
                  />
                  <button class="eye-btn" type="button" @click="togglePassword">
                    <svg
                      v-if="!passwordVisible"
                      class="icon"
                      viewBox="0 0 24 24"
                      fill="none"
                    >
                      <path
                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                      <path
                        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                    <svg v-else class="icon" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                  </button>
                </div>
              </div>

              <div class="row">
                <label class="remember">
                  <input v-model="rememberMe" type="checkbox" />
                  <span>记住我</span>
                </label>
                <button class="link" type="button" @click="goForgot">
                  忘记密码？
                </button>
              </div>

              <button
                class="btn btn-primary"
                type="submit"
                :disabled="pwdSubmitting"
              >
                <span v-if="!pwdSubmitting">登 录</span>
                <span v-else class="loading">
                  <svg class="spinner" viewBox="0 0 24 24">
                    <circle class="spinner-track" cx="12" cy="12" r="10" />
                    <path class="spinner-head" d="M4 12a8 8 0 018-8" />
                  </svg>
                </span>
              </button>
            </form>

            <div class="divider" />

            <!-- <div class="register">
              <span>还没有账号？</span>
              <button class="link strong" type="button" @click="goRegister">
                立即注册
              </button>
            </div> -->
          </div>

          <div v-else>
            <form class="form" @submit.prevent="submitRegister">
              <div class="field">
                <div class="label">用户名</div>
                <input
                  v-model.trim="regForm.name"
                  class="input input-elegant"
                  type="text"
                  placeholder="请输入用户名"
                />
              </div>

              <div class="field">
                <div class="label">手机号码</div>
                <div class="input-wrap">
                  <input
                    v-model.trim="regForm.phone"
                    class="input input-elegant"
                    type="tel"
                    inputmode="numeric"
                    maxlength="11"
                    placeholder="请输入11位手机号"
                    @input="onRegPhoneInput"
                  />
                  <div class="input-icon">
                    <svg class="icon" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                  </div>
                </div>
              </div>

              <div class="field">
                <div class="label">密码</div>
                <div class="input-wrap">
                  <input
                    v-model="regForm.upass"
                    class="input input-elegant"
                    :type="regPasswordVisible ? 'text' : 'password'"
                    placeholder="请输入密码"
                  />
                  <button
                    class="eye-btn"
                    type="button"
                    @click="toggleRegPassword"
                  >
                    <svg
                      v-if="!regPasswordVisible"
                      class="icon"
                      viewBox="0 0 24 24"
                      fill="none"
                    >
                      <path
                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                      <path
                        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                    <svg v-else class="icon" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                  </button>
                </div>
              </div>

              <div class="field">
                <div class="label">确认密码</div>
                <div class="input-wrap">
                  <input
                    v-model="regForm.confpwd"
                    class="input input-elegant"
                    :type="regConfirmPasswordVisible ? 'text' : 'password'"
                    placeholder="请再次输入密码"
                  />
                  <button
                    class="eye-btn"
                    type="button"
                    @click="toggleRegConfirmPassword"
                  >
                    <svg
                      v-if="!regConfirmPasswordVisible"
                      class="icon"
                      viewBox="0 0 24 24"
                      fill="none"
                    >
                      <path
                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                      <path
                        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                    <svg v-else class="icon" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                  </button>
                </div>
              </div>

              <div class="field">
                <div class="label">验证码</div>
                <div class="code-row">
                  <input
                    v-model.trim="regForm.verifyCode"
                    class="input input-elegant"
                    type="text"
                    inputmode="numeric"
                    maxlength="6"
                    placeholder="请输入6位验证码"
                  />
                  <button
                    class="code-btn"
                    type="button"
                    :disabled="regCodeSendingDisabled"
                    @click="getRegisterCode"
                  >
                    {{ regCodeBtnText }}
                  </button>
                </div>
              </div>

              <button
                class="btn btn-primary"
                type="submit"
                :disabled="registerSubmitting"
              >
                <span v-if="!registerSubmitting">注 册</span>
                <span v-else class="loading">
                  <svg class="spinner" viewBox="0 0 24 24">
                    <circle class="spinner-track" cx="12" cy="12" r="10" />
                    <path class="spinner-head" d="M4 12a8 8 0 018-8" />
                  </svg>
                </span>
              </button>
            </form>

            <div class="divider" />

            <div class="register">
              <span>已有账号？</span>
              <button class="link strong" type="button" @click="backToLogin">
                去登录
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="footer-copyright">
      <div class="copyright_content">
        <img
          src="http://mlwccy.oss-cn-qingdao.aliyuncs.com/2024-10-09/eea5d0b788234884a0a354ccc1d20bc3.png"
          alt=""
        />
        <a id="" target="_blank" href="http://beian.miit.gov.cn">
          【津ICP备2025032757号-1】 Copyright 2025
          联帮帮物流科技(天津)有限责任公司版权所有</a
        >
      </div>
    </div>

    <vxe-modal
      v-model="verificationFlag"
      :show-header="false"
      title="验证码"
      width="450"
      height="250"
      resize
      :transfer="true"
      :z-index="5000"
    >
      <verification ref="verificationRef" @receive="receive" @cancel="cancel" />
    </vxe-modal>
  </div>
</template>

<script>
import request from "@/utils/request";
import {
  sendVerificationCode,
  register,
  GetRegisterVerifycode
} from "@/api/user";
import verification from "@/views/register/components/verification";
import Fingerprint2 from "fingerprintjs2";
import { validatePhone } from "@/utils/validate.js";
const Base64 = require("js-base64").Base64;

export default {
  name: "TianjinIndex",
  components: { verification },
  data() {
    return {
      loginImg: "",
      isMobile: false,
      viewMode: "login",
      activeTab: "password",
      branchCode: "",
      loginAdminPhone: "",
      partyGroupList: [],
      isLoginNeedSMS: "",
      passwordVisible: false,
      regPasswordVisible: false,
      regConfirmPasswordVisible: false,
      rememberMe: false,
      smsSubmitting: false,
      pwdSubmitting: false,
      registerSubmitting: false,
      verificationScene: "login",
      smsForm: {
        clientId: "3db37dffa163e9d2a1829c20fandriod",
        clientSecret: "98a7f887bca011e98e15fa163e6557b9",
        loginType: "SHIPMENT",
        partyType: "2",
        time: "",
        branchCode: "",
        phone: "",
        verifyCode: ""
      },
      pwdForm: {
        client_id: "4111ea97dffa163e9d2a183dea15fweb",
        client_secret: "f11ffb5c041b11ea97dffa163e9d2a29",
        grant_type: "password",
        response_type: "token",
        device_id: "0000000000",
        username: "",
        userPwd: "",
        password: "",
        time: "",
        branchCode: "",
        phoneCode: ""
      },
      regForm: {
        phone: "",
        name: "",
        verifyCode: "",
        upass: "",
        confpwd: "",
        branchCode: "",
        partyType: 3,
        imageCode: ""
      },
      regCationCode: {
        partyGroupCode: "",
        phone: "",
        imageCode: ""
      },
      verificationFlag: false,
      imgurl: "",
      redirect: undefined,
      otherQuery: {},
      sliderDragging: false,
      sliderVerified: false,
      sliderX: 0,
      sliderMaxX: 0,
      sliderDocBound: false,
      codeCountdown: 0,
      codeTimer: null,
      regCodeCountdown: 0,
      regCodeTimer: null,
      particleRafId: 0,
      particleCtx: null,
      particleW: 0,
      particleH: 0,
      particleDpr: 1,
      particleList: [],
      particleMouseX: 0,
      particleMouseY: 0,
      particleMouseActive: false,
      particleHidden: false,
      particleTick: 0,
      particleTargetX: 0,
      particleTargetY: 0,
      particleStartT: 0
    };
  },
  computed: {
    headerTitle() {
      return this.viewMode === "register" ? "注册账号" : "欢迎回来";
    },
    headerSub() {
      return this.viewMode === "register"
        ? "填写信息完成注册后即可登录"
        : "登录您的账户以继续使用";
    },
    sliderText() {
      return this.sliderVerified ? "验证通过 ✓" : "向右滑动验证 →";
    },
    sliderThumbStyle() {
      return { transform: `translateX(${this.sliderX}px)` };
    },
    sliderProgressStyle() {
      const width = this.sliderX + 44;
      return { width: `${Math.max(0, width)}px` };
    },
    codeSendingDisabled() {
      return this.codeCountdown > 0 || !this.sliderVerified;
    },
    codeBtnText() {
      return this.codeCountdown > 0 ? `${this.codeCountdown}秒` : "获取验证码";
    },
    regCodeSendingDisabled() {
      return this.regCodeCountdown > 0;
    },
    regCodeBtnText() {
      return this.regCodeCountdown > 0
        ? `${this.regCodeCountdown}秒`
        : "获取验证码";
    }
  },
  watch: {
    $route: {
      handler: function(route) {
        const query = route && route.query;
        if (query) {
          this.redirect = query.redirect;
          this.otherQuery = this.getOtherQuery(query);
        }
      },
      immediate: true
    }
  },
  created() {
    this.getFingerprint();
    request({
      url: "/ntocc-basic-api/partyGroup/getDefault",
      method: "get"
    }).then(response => {
      this.loginImg = require("@/assets/icon/tianjinlogo.png"); // response.data.loginLogoUrl;
      this.$store.dispatch("enterprise/setEnterprise", response.data);
      localStorage.setItem("platformName", response.data.platformName);
      this.isLoginNeedSMS = response.data.isLoginNeedSMS;
    });

    request({
      url: "/ntocc-basic-api/partyGroup/list",
      method: "get"
    }).then(response => {
      this.partyGroupList = response.data || [];
      if (this.partyGroupList.length > 0) {
        this.branchCode = this.partyGroupList[0].code;
        this.loginAdminPhone = this.partyGroupList[0].telephone;
        this.smsForm.branchCode = this.branchCode;
        this.pwdForm.branchCode = this.branchCode;
        this.regForm.branchCode = this.branchCode;
      }
    });
  },
  mounted() {
    if (this.$route && this.$route.query && this.$route.query.uid) {
      const uid = this.$route.query.uid;
      if (uid !== "" && typeof uid !== "undefined") {
        try {
          const jmUuid = Base64.decode(uid);
          const dxUid = this.sortString(decodeURIComponent(jmUuid));
          const jmUid = Base64.decode(dxUid);
          const oneSub = this.getCaption(jmUid, 0);
          this.pwdForm.username = this.getCaption(oneSub, 0);
          this.pwdForm.userPwd = this.getCaption(jmUid, 1);
          this.activeTab = "password";
          this.$nextTick(() => this.submitPassword());
        } catch (e) {}
      }
    }
    this.updateMobile();
    window.addEventListener("resize", this.onResize, { passive: true });
    // document.addEventListener("mousemove", this.onDocMove, { passive: false });
    // document.addEventListener("mouseup", this.onDocUp, { passive: true });
    // document.addEventListener("touchmove", this.onDocMove, { passive: false });
    // document.addEventListener("touchend", this.onDocUp, { passive: true });
    // window.addEventListener("mousemove", this.onParticleMouseMove, {
    //   passive: true
    // });
    // window.addEventListener("mouseleave", this.onParticleMouseLeave, {
    //   passive: true
    // });
    // window.addEventListener("touchmove", this.onParticleTouchMove, {
    //   passive: true
    // });
    document.addEventListener("visibilitychange", this.onVisibilityChange, {
      passive: true
    });
    this.initParticles();
    this.$nextTick(() => {
      this.updateSliderMetrics();
    });
  },
  destroyed() {
    window.removeEventListener("resize", this.onResize);
    this.unbindSliderDocEvents();
    // window.removeEventListener("mousemove", this.onParticleMouseMove);
    // window.removeEventListener("mouseleave", this.onParticleMouseLeave);
    // window.removeEventListener("touchmove", this.onParticleTouchMove);
    document.removeEventListener("visibilitychange", this.onVisibilityChange);
    this.destroyParticles();
    if (this.codeTimer) {
      clearInterval(this.codeTimer);
      this.codeTimer = null;
    }
    if (this.regCodeTimer) {
      clearInterval(this.regCodeTimer);
      this.regCodeTimer = null;
    }
  },
  methods: {
    getFingerprint() {
      Fingerprint2.get(components => {
        const values = components.map(function(component, index) {
          if (index === 0) {
            return component.value.replace(/\bNetType\/\w+\b/, "");
          }
          return component.value;
        });
        const murmur = Fingerprint2.x64hash128(values.join(""), 31);
        this.pwdForm.device_id = murmur;
      });
    },
    switchTab(tab) {
      this.activeTab = tab;
      if (tab === "sms") {
        this.$nextTick(() => this.updateSliderMetrics());
      }
    },
    togglePassword() {
      this.passwordVisible = !this.passwordVisible;
    },
    toggleRegPassword() {
      this.regPasswordVisible = !this.regPasswordVisible;
    },
    toggleRegConfirmPassword() {
      this.regConfirmPasswordVisible = !this.regConfirmPasswordVisible;
    },
    goRegister() {
      this.viewMode = "register";
      this.activeTab = "password";
      if (!this.regForm.branchCode) {
        this.regForm.branchCode = this.branchCode;
      }
    },
    backToLogin() {
      this.viewMode = "login";
      this.activeTab = "password";
    },
    goForgot() {
      this.$router.push("/forgotPWD");
    },
    getOtherQuery(query) {
      return Object.keys(query).reduce((acc, cur) => {
        if (cur !== "redirect") acc[cur] = query[cur];
        return acc;
      }, {});
    },
    getCaption(obj, state) {
      const index = obj.lastIndexOf("_");
      if (state === 0) return obj.substring(0, index);
      return obj.substring(index + 1, obj.length);
    },
    sortString(str) {
      return str
        .split("")
        .reverse("")
        .join("");
    },
    formatTimeYYYYMMDDHHmmss(date) {
      const pad = n => String(n).padStart(2, "0");
      return (
        date.getFullYear() +
        pad(date.getMonth() + 1) +
        pad(date.getDate()) +
        pad(date.getHours()) +
        pad(date.getMinutes()) +
        pad(date.getSeconds())
      );
    },
    updateMobile() {
      this.isMobile = window.innerWidth < 1024;
    },
    onResize() {
      this.updateMobile();
      this.updateSliderMetrics();
      this.resizeParticles();
    },
    onVisibilityChange() {
      this.particleHidden = document.hidden;
      if (this.particleHidden) {
        if (this.particleRafId) cancelAnimationFrame(this.particleRafId);
        this.particleRafId = 0;
      } else {
        this.animateParticles();
      }
    },
    onParticleMouseMove(e) {
      const x = e ? e.clientX : 0;
      const y = e ? e.clientY : 0;
      const w = this.particleW || window.innerWidth || 1;
      const h = this.particleH || window.innerHeight || 1;
      this.particleMouseX = (x - w / 2) / 100;
      this.particleMouseY = (y - h / 2) / 100;
      this.particleMouseActive = true;
    },
    onParticleMouseLeave() {
      this.particleMouseActive = false;
    },
    onParticleTouchMove(e) {
      if (!e || !e.touches || !e.touches[0]) return;
      const x = e.touches[0].clientX;
      const y = e.touches[0].clientY;
      const w = this.particleW || window.innerWidth || 1;
      const h = this.particleH || window.innerHeight || 1;
      this.particleMouseX = (x - w / 2) / 100;
      this.particleMouseY = (y - h / 2) / 100;
      this.particleMouseActive = true;
    },
    initParticles() {
      const canvas = this.$refs.particleCanvas;
      if (!canvas) return;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      this.particleCtx = ctx;
      this.particleStartT =
        window.performance && typeof window.performance.now === "function"
          ? window.performance.now()
          : Date.now();
      this.particleTargetX = 0;
      this.particleTargetY = 0;
      this.resizeParticles();
      this.buildParticles();
      this.animateParticles();
    },
    destroyParticles() {
      if (this.particleRafId) cancelAnimationFrame(this.particleRafId);
      this.particleRafId = 0;
      this.particleList = [];
      this.particleCtx = null;
    },
    resizeParticles() {
      const canvas = this.$refs.particleCanvas;
      if (!canvas) return;
      const w = window.innerWidth || 1;
      const h = window.innerHeight || 1;
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      this.particleW = w;
      this.particleH = h;
      this.particleDpr = dpr;
      canvas.width = Math.floor(w * dpr);
      canvas.height = Math.floor(h * dpr);
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
      if (this.particleCtx) {
        this.particleCtx.setTransform(dpr, 0, 0, dpr, 0, 0);
      }
      if (this.particleList && this.particleList.length) {
        const targetCount = this.getParticleCount();
        if (this.particleList.length > targetCount) {
          this.particleList.splice(targetCount);
        } else if (this.particleList.length < targetCount) {
          for (let i = this.particleList.length; i < targetCount; i += 1) {
            this.particleList.push(this.createParticle(true));
          }
        }
      }
    },
    getParticleCount() {
      return this.isMobile ? 600 : 1000;
    },
    buildParticles() {
      const count = this.getParticleCount();
      this.particleList = [];
      for (let i = 0; i < count; i += 1) {
        this.particleList.push(this.createParticle());
      }
    },
    createParticle() {
      const x = (Math.random() - 0.5) * 50;
      const y = (Math.random() - 0.5) * 50;
      const z = (Math.random() - 0.5) * 30;
      const cr = Math.round((0.3 + Math.random() * 0.3) * 255);
      const cg = Math.round((0.4 + Math.random() * 0.3) * 255);
      const cb = Math.round((0.9 + Math.random() * 0.1) * 255);
      const sf = 0.55 + Math.random() * 0.75;
      return {
        x,
        y,
        z,
        baseY: y,
        cr,
        cg,
        cb,
        sf
      };
    },
    animateParticles() {
      if (this.particleHidden) return;
      if (!this.particleCtx) return;
      const ctx = this.particleCtx;
      const w = this.particleW;
      const h = this.particleH;
      const now =
        window.performance && typeof window.performance.now === "function"
          ? window.performance.now()
          : Date.now();
      this.particleTick += 1;
      const elapsedTime = this.particleStartT
        ? (now - this.particleStartT) / 1000
        : 0;
      ctx.clearRect(0, 0, w, h);
      const mouseX = this.particleMouseActive ? this.particleMouseX : 0;
      const mouseY = this.particleMouseActive ? this.particleMouseY : 0;

      this.particleTargetX += (mouseX - this.particleTargetX) * 0.05;
      this.particleTargetY += (mouseY - this.particleTargetY) * 0.05;

      const rotY = this.particleTargetX * 0.5 + elapsedTime * 0.02;
      const rotX = this.particleTargetY * 0.3;
      const cosY = Math.cos(rotY);
      const sinY = Math.sin(rotY);
      const cosX = Math.cos(rotX);
      const sinX = Math.sin(rotX);

      const cameraZ = 20;
      const baseSize = 0.3;
      const worldScale = (Math.min(w, h) / 50) * 1.1;
      const list = this.particleList || [];

      ctx.save();
      ctx.globalCompositeOperation = "lighter";
      ctx.globalAlpha = 0.45;
      for (let i = 0; i < list.length; i += 1) {
        const p = list[i];
        const wave = Math.sin(elapsedTime * 0.3 + p.x * 0.05) * 0.008;
        const x0 = p.x;
        const y0 = p.baseY + wave;
        const z0 = p.z;

        const x1 = x0 * cosY + z0 * sinY;
        const z1 = -x0 * sinY + z0 * cosY;
        const y2 = y0 * cosX - z1 * sinX;
        const z2 = y0 * sinX + z1 * cosX;

        const denom = cameraZ - z2;
        if (denom <= 0.1) continue;
        const scale = cameraZ / denom;
        const sx = w / 2 + x1 * scale * worldScale;
        const sy = h / 2 + y2 * scale * worldScale;
        const size = Math.max(
          1.5,
          Math.min(10, baseSize * scale * worldScale * (p.sf || 1))
        );

        ctx.fillStyle = `rgba(${p.cr}, ${p.cg}, ${p.cb}, 1)`;
        ctx.fillRect(sx - size / 2, sy - size / 2, size, size);
      }
      ctx.restore();

      this.particleRafId = requestAnimationFrame(this.animateParticles);
    },
    onPhoneInput() {
      this.smsForm.phone = (this.smsForm.phone || "")
        .replace(/\D/g, "")
        .slice(0, 11);
      if (!this.sliderVerified) {
        this.resetSlider();
      }
    },
    onRegPhoneInput() {
      this.regForm.phone = (this.regForm.phone || "")
        .replace(/\D/g, "")
        .slice(0, 11);
    },
    updateSliderMetrics() {
      const el = this.$refs.slider;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const maxX = Math.max(0, rect.width - 52);
      this.sliderMaxX = maxX;
      if (this.sliderVerified) {
        this.sliderX = maxX;
      } else {
        this.sliderX = 0;
      }
    },
    resetSlider() {
      this.sliderDragging = false;
      this.sliderVerified = false;
      this.sliderX = 0;
    },
    getClientX(evt) {
      if (evt && evt.touches && evt.touches[0]) return evt.touches[0].clientX;
      if (evt && evt.changedTouches && evt.changedTouches[0]) {
        return evt.changedTouches[0].clientX;
      }
      return evt ? evt.clientX : 0;
    },
    bindSliderDocEvents() {
      if (this.sliderDocBound) return;
      this.sliderDocBound = true;
      document.addEventListener("mousemove", this.onDocMove, {
        passive: false
      });
      document.addEventListener("mouseup", this.onDocUp, { passive: true });
      document.addEventListener("touchmove", this.onDocMove, {
        passive: false
      });
      document.addEventListener("touchend", this.onDocUp, { passive: true });
      document.addEventListener("touchcancel", this.onDocUp, { passive: true });
    },
    unbindSliderDocEvents() {
      if (!this.sliderDocBound) return;
      this.sliderDocBound = false;
      document.removeEventListener("mousemove", this.onDocMove);
      document.removeEventListener("mouseup", this.onDocUp);
      document.removeEventListener("touchmove", this.onDocMove);
      document.removeEventListener("touchend", this.onDocUp);
      document.removeEventListener("touchcancel", this.onDocUp);
    },
    onThumbDown(e) {
      if (this.sliderVerified) return;
      this.updateSliderMetrics();
      this.sliderDragging = true;
      this.bindSliderDocEvents();
      e.preventDefault();
    },
    onDocMove(e) {
      if (!this.sliderDragging || this.sliderVerified) return;
      const el = this.$refs.slider;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const clientX = this.getClientX(e);
      let x = clientX - rect.left - 44;
      if (x < 0) x = 0;
      if (x > this.sliderMaxX) x = this.sliderMaxX;
      this.sliderX = x;
      if (x >= this.sliderMaxX - 5) {
        this.sliderVerified = true;
        this.sliderDragging = false;
        this.sliderX = this.sliderMaxX;
        this.unbindSliderDocEvents();
      }
      if (e && e.cancelable) e.preventDefault();
    },
    onDocUp() {
      if (!this.sliderDragging) return;
      if (!this.sliderVerified) {
        this.sliderX = 0;
      }
      this.sliderDragging = false;
      this.unbindSliderDocEvents();
    },
    async sendCode() {
      if (this.codeCountdown > 0) return;
      if (!this.sliderVerified) {
        this.$message.warning("请先完成滑动验证");
        return;
      }
      const rawPhone = this.smsForm.phone;
      let phone = rawPhone;
      if (!validatePhone(phone)) {
        phone = this.loginAdminPhone;
      }
      if (!phone || phone.length !== 11) {
        this.$message.error("请输入正确的手机号码");
        return;
      }
      try {
        await sendVerificationCode({
          phone,
          partyGroupCode: this.smsForm.branchCode || this.branchCode
        });
        this.$message.success("验证码已发送");
        this.startCountdown(60);
      } catch (e) {
        this.$message.error("验证码发送失败");
      }
    },
    startCountdown(seconds) {
      this.codeCountdown = seconds;
      if (this.codeTimer) clearInterval(this.codeTimer);
      this.codeTimer = setInterval(() => {
        this.codeCountdown -= 1;
        if (this.codeCountdown <= 0) {
          this.codeCountdown = 0;
          clearInterval(this.codeTimer);
          this.codeTimer = null;
        }
      }, 1000);
    },
    getNeedImageVerify() {
      this.verificationScene = "login";
      return request({
        url: "/ntocc-basic-api/authorize/imageVerify",
        method: "get",
        params: {
          username: this.pwdForm.username,
          branchCode: this.pwdForm.branchCode || this.branchCode
        }
      }).then(response => {
        if (response && response.data && response.data.needImage === "1") {
          this.verificationFlag = true;
          const baseUrl = this.BASE_API_URL || "";
          this.imgurl =
            baseUrl +
            "ntocc-tps-api/sms/getVerificationCodeImage?phone=" +
            this.pwdForm.username +
            "&imageWith=300&imageHeigth=100&t=" +
            new Date().getTime();
          this.$nextTick(() => {
            const ref = this.$refs.verificationRef;
            if (ref && typeof ref.getVerificationCodeImage === "function") {
              ref.getVerificationCodeImage(this.imgurl);
            }
          });
          return;
        }
        return this.doPasswordLogin();
      });
    },
    cancel() {
      this.verificationFlag = false;
      this.verificationScene = "login";
    },
    receive(imageCode) {
      this.verificationFlag = false;
      if (this.verificationScene === "register") {
        this.regCationCode.imageCode = imageCode;
        return this.sendRegisterVerifyCode();
      }
      this.pwdForm.phoneCode = imageCode;
      return this.doPasswordLogin();
    },
    getRegisterCode() {
      if (this.regCodeCountdown > 0) return;
      const phone = (this.regForm.phone || "").trim();
      if (!validatePhone(phone)) {
        this.$message.error("请输入正确的手机号码");
        return;
      }
      const partyGroupCode = this.regForm.branchCode || this.branchCode;
      this.regCationCode.partyGroupCode = partyGroupCode;
      this.regCationCode.phone = phone;
      const baseUrl = this.BASE_API_URL || "";
      this.imgurl =
        `${baseUrl}ntocc-tps-api/sms/getVerificationCodeImage?phone=` +
        phone +
        "&imageWith=300&imageHeigth=100&t=" +
        new Date().getTime();
      this.verificationScene = "register";
      this.verificationFlag = true;
      this.$nextTick(() => {
        const ref = this.$refs.verificationRef;
        if (ref && typeof ref.getVerificationCodeImage === "function") {
          ref.getVerificationCodeImage(this.imgurl);
        }
      });
    },
    async sendRegisterVerifyCode() {
      try {
        const res = await GetRegisterVerifycode(this.regCationCode);
        if (res && String(res.status) === "0") {
          this.$message.success("验证码已发送");
          this.startRegCountdown(60);
        } else {
          this.$message.error((res && res.msg) || "验证码发送失败");
        }
      } catch (e) {
        this.$message.error("验证码发送失败");
      } finally {
        this.verificationScene = "login";
      }
    },
    startRegCountdown(seconds) {
      this.regCodeCountdown = seconds;
      if (this.regCodeTimer) clearInterval(this.regCodeTimer);
      this.regCodeTimer = setInterval(() => {
        this.regCodeCountdown -= 1;
        if (this.regCodeCountdown <= 0) {
          this.regCodeCountdown = 0;
          clearInterval(this.regCodeTimer);
          this.regCodeTimer = null;
        }
      }, 1000);
    },
    async doSmsLogin() {
      const time =
        this.$XEUtils && this.$XEUtils.toDateString
          ? this.$XEUtils.toDateString(new Date(), "yyyyMMddHHmmss")
          : this.formatTimeYYYYMMDDHHmmss(new Date());
      const param = Object.assign({}, this.smsForm, {
        time,
        branchCode: this.smsForm.branchCode || this.branchCode
      });
      const passWordOverdue = await this.$store.dispatch(
        "user/applogin",
        param
      );
      if (passWordOverdue) {
        this.$message({
          message: "密码未定期更换，请及时修改密码！",
          type: "warning"
        });
      }
      await request({
        url: "/ntocc-basic-api/middleStation/getMsg",
        method: "get"
      }).then(res => {
        if (res && res.msg) {
          this.$message({ message: res.msg, type: "warning" });
        }
      });
      this.$router.push({
        path: this.redirect || "/",
        query: this.otherQuery
      });
    },
    async doPasswordLogin() {
      const mw = "X2FycGFf";
      const jm = Base64.decode(mw);
      const time =
        this.$XEUtils && this.$XEUtils.toDateString
          ? this.$XEUtils.toDateString(new Date(), "yyyyMMddHHmmss")
          : this.formatTimeYYYYMMDDHHmmss(new Date());
      const password = Base64.encode(this.pwdForm.userPwd + jm + time);
      const param = Object.assign({}, this.pwdForm, {
        time,
        password,
        branchCode: this.pwdForm.branchCode || this.branchCode
      });
      const passWordOverdue = await this.$store.dispatch("user/login", param);
      if (passWordOverdue) {
        this.$message({
          message: "密码未定期更换，请及时修改密码！",
          type: "warning"
        });
      }
      await request({
        url: "/ntocc-basic-api/middleStation/getMsg",
        method: "get"
      }).then(res => {
        if (res && res.msg) {
          this.$message({ message: res.msg, type: "warning" });
        }
      });
      this.$router.push({
        path: this.redirect || "/",
        query: this.otherQuery
      });
    },
    async submitSms() {
      if (this.smsSubmitting) return;
      if (!this.sliderVerified) {
        this.$message.warning("请先完成滑动验证");
        return;
      }
      const phone = this.smsForm.phone;
      if (!validatePhone(phone)) {
        this.$message.error("请输入正确的手机号码");
        return;
      }
      if (!this.smsForm.verifyCode || this.smsForm.verifyCode.length < 4) {
        this.$message.error("请输入验证码");
        return;
      }
      this.smsSubmitting = true;
      try {
        await this.doSmsLogin();
      } catch (e) {
        // this.$message.error("登录失败，请检查验证码或账号信息");
        this.resetSlider();
      } finally {
        this.smsSubmitting = false;
      }
    },
    async submitPassword() {
      if (this.pwdSubmitting) return;
      if (!this.pwdForm.username) {
        this.$message.error("请输入账号");
        return;
      }
      if (!this.pwdForm.userPwd) {
        this.$message.error("请输入密码");
        return;
      }
      this.pwdSubmitting = true;
      try {
        if (this.isLoginNeedSMS === "yes") {
          await this.doPasswordLogin();
        } else {
          await this.getNeedImageVerify();
        }
      } catch (e) {
        // this.$message.error("登录失败，请检查账号或密码");
      } finally {
        this.pwdSubmitting = false;
      }
    },
    async submitRegister() {
      if (this.registerSubmitting) return;
      const name = (this.regForm.name || "").trim();
      const phone = (this.regForm.phone || "").trim();
      const verifyCode = (this.regForm.verifyCode || "").trim();
      if (!name) {
        this.$message.error("请输入用户名");
        return;
      }
      if (!validatePhone(phone)) {
        this.$message.error("请输入正确的手机号码");
        return;
      }
      if (!this.regForm.upass) {
        this.$message.error("请输入密码");
        return;
      }
      if (!this.regForm.confpwd) {
        this.$message.error("请输入确认密码");
        return;
      }
      if (this.regForm.upass !== this.regForm.confpwd) {
        this.$message.error("两次密码输入不一致");
        return;
      }
      if (!verifyCode || verifyCode.length < 4) {
        this.$message.error("请输入验证码");
        return;
      }

      const formData = Object.assign({}, this.regForm, {
        name,
        phone,
        verifyCode,
        branchCode: this.regForm.branchCode || this.branchCode
      });
      formData.upass = Base64.encode(this.regForm.upass);
      formData.confpwd = Base64.encode(this.regForm.confpwd);

      this.registerSubmitting = true;
      try {
        const res = await register(formData);
        if (res && String(res.status) === "0") {
          this.$message.success("注册成功，请登录");
          this.viewMode = "login";
          this.activeTab = "password";
          this.pwdForm.username = phone;
          this.pwdForm.userPwd = "";
        } else {
          this.$message.error((res && res.msg) || "注册失败");
        }
      } catch (e) {
        this.$message.error("注册失败");
      } finally {
        this.registerSubmitting = false;
      }
    }
  }
};
</script>

<style scoped>
.copyright_content {
  display: flex;
  align-items: center;
  width: 100%;
  justify-content: center;
}

.tj-login {
  position: relative;
  min-height: 100vh;
  color: #0f172a;
  font-family: "Noto Sans SC", "Inter", -apple-system, BlinkMacSystemFont,
    "Segoe UI", Roboto, "Helvetica Neue", Arial, "PingFang SC",
    "Hiragino Sans GB", "Microsoft YaHei", sans-serif;
  overflow-x: hidden;
  -webkit-user-select: none;
  -ms-user-select: none;
  user-select: none;
}

.tj-login input,
.tj-login textarea,
.tj-login [contenteditable="true"] {
  -webkit-user-select: text;
  -ms-user-select: text;
  user-select: text;
}

.bg {
  position: fixed;
  inset: 0;
  z-index: 0;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
}

.bg-canvas {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
}

.bg-dots {
  position: absolute;
  inset: 0;
  background-image: radial-gradient(
      circle at 20% 30%,
      rgba(79, 70, 229, 0.12) 0 2px,
      transparent 3px
    ),
    radial-gradient(
      circle at 80% 20%,
      rgba(124, 58, 237, 0.1) 0 2px,
      transparent 3px
    ),
    radial-gradient(
      circle at 65% 70%,
      rgba(59, 130, 246, 0.1) 0 2px,
      transparent 3px
    ),
    radial-gradient(
      circle at 35% 85%,
      rgba(79, 70, 229, 0.1) 0 2px,
      transparent 3px
    );
  background-size: 260px 260px;
  animation: floatDots 14s ease-in-out infinite;
  pointer-events: none;
  opacity: 0.2;
}

@keyframes floatDots {
  0% {
    transform: translate3d(0, 0, 0);
  }

  50% {
    transform: translate3d(-18px, 10px, 0);
  }

  100% {
    transform: translate3d(0, 0, 0);
  }
}

.container {
  position: relative;
  z-index: 1;
  max-width: 1180px;
  margin: 0 auto;
  padding: 48px 24px 24px;
  display: grid;
  grid-template-columns: 1fr 420px;
  gap: 219px;
  align-items: center;
  min-height: calc(100vh - 100px);
}

@media (max-width: 1023px) {
  .container {
    grid-template-columns: 1fr;
    gap: 20px;
    padding: 32px 18px 24px;
    min-height: calc(100vh - 120px);
  }
}

.left {
  display: flex;
  flex-direction: column;
  gap: 36px;
}

.brand-text-gradient {
  background: linear-gradient(90deg, #4f46e5 0%, #7c3aed 50%, #3b82f6 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  color: transparent;
}

.logo-img {
  width: 200px;
  height: auto;
  object-fit: contain;
  filter: drop-shadow(0 4px 12px rgba(79, 70, 229, 0.15));
}

.logo-img-mobile {
  width: 160px;
  height: auto;
  object-fit: contain;
  filter: drop-shadow(0 4px 12px rgba(79, 70, 229, 0.15));
}

.logo-fallback {
  font-size: 44px;
  font-weight: 900;
  letter-spacing: 0.02em;
  line-height: 1.1;
}

.slogan-cn {
  font-size: 56px;
  line-height: 1.05;
  font-weight: 900;
  letter-spacing: -0.02em;
}

.slogan-cn-1 {
  color: #0f172a;
}

.slogan-cn-2 {
  margin-top: 6px;
}

.slogan-en {
  margin-top: 14px;
  font-size: 18px;
  font-weight: 600;
  color: #64748b;
  line-height: 1.35;
}

.deco {
  margin-top: 14px;
  display: flex;
  align-items: center;
  gap: 12px;
}

.deco-bar {
  width: 64px;
  height: 4px;
  border-radius: 999px;
  background: linear-gradient(90deg, #4f46e5 0%, #7c3aed 100%);
}

.deco-dot {
  width: 6px;
  height: 6px;
  border-radius: 999px;
  background: #a855f7;
}

.deco-line {
  width: 96px;
  height: 1px;
  background: #e2e8f0;
}

.value {
  max-width: 420px;
  color: #64748b;
  line-height: 1.6;
}

.value-sub {
  color: #94a3b8;
}

.right {
  display: flex;
  justify-content: flex-end;
}

@media (max-width: 1023px) {
  .right {
    justify-content: center;
  }
}

.card {
  width: 100%;
  max-width: 420px;
  border-radius: 24px;
  padding: 28px;
}

@media (max-width: 1023px) {
  .card {
    padding: 24px;
  }
}

.glass-card {
  background: rgba(255, 255, 255, 0.86);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(226, 232, 240, 0.85);
  box-shadow: 0 10px 40px -10px rgba(0, 0, 0, 0.08),
    0 0 0 1px rgba(255, 255, 255, 0.6) inset;
  min-height: 528px;
}

.mobile-brand {
  text-align: center;
  margin-bottom: 20px;
}

.mobile-sub {
  margin-top: 10px;
  font-size: 12px;
  color: #64748b;
}

.header {
  margin-bottom: 18px;
}

.h-title {
  font-size: 24px;
  font-weight: 800;
  color: #0f172a;
}

.h-sub {
  margin-top: 6px;
  font-size: 13px;
  color: #64748b;
}

.tabs {
  display: flex;
  padding: 4px;
  border-radius: 999px;
  background: #f1f5f9;
  gap: 4px;
  margin-bottom: 18px;
}

.tab-btn {
  flex: 1;
  height: 38px;
  border-radius: 999px;
  border: 0;
  background: transparent;
  cursor: pointer;
  font-size: 13px;
  color: #475569;
  transition: all 0.2s ease;
}

.tab-btn.active {
  background: #ffffff;
  color: #4f46e5;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  font-weight: 700;
}

.form {
  display: grid;
  gap: 14px;
}

.field .label {
  font-size: 13px;
  font-weight: 600;
  color: #334155;
  margin-bottom: 8px;
}

.input-wrap {
  position: relative;
}

.input {
  width: 100%;
  height: 44px;
  border-radius: 14px;
  padding: 0 14px;
  font-size: 14px;
  color: #0f172a;
}

.input-elegant {
  background: #f8fafc;
  border: 1.5px solid #e2e8f0;
  transition: all 0.2s ease;
}

.input-elegant:focus {
  background: #ffffff;
  border-color: #4f46e5;
  box-shadow: 0 0 0 4px rgba(79, 70, 229, 0.08);
  outline: none;
}

.input-icon {
  position: absolute;
  right: 12px;
  top: 10px;
  color: #94a3b8;
  pointer-events: none;
}

.eye-btn {
  position: absolute;
  right: 10px;
  top: 9px;
  height: 26px;
  width: 34px;
  border: 0;
  background: transparent;
  color: #94a3b8;
  cursor: pointer;
  transition: color 0.2s ease;
}

.eye-btn:hover {
  color: #4f46e5;
}

.icon {
  width: 20px;
  height: 20px;
}

.icon.verified {
  color: #4f46e5;
}

.slider {
  position: relative;
  height: 48px;
  border-radius: 999px;
  background: #f1f5f9;
  overflow: hidden;
  user-select: none;
}

.slider-text {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  color: #64748b;
  font-weight: 600;
  z-index: 2;
  transition: color 0.2s ease;
}

.slider-text.verified {
  color: #4f46e5;
}

.slider-progress {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 0;
  background: rgba(79, 70, 229, 0.1);
  border-radius: 999px;
  z-index: 1;
  transition: width 0.08s linear;
}

.slider-progress.verified {
  background: rgba(79, 70, 229, 0.16);
}

.slider-thumb {
  position: absolute;
  left: 4px;
  top: 4px;
  bottom: 4px;
  width: 40px;
  border-radius: 999px;
  background: #ffffff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #475569;
  z-index: 3;
  transition: transform 0.08s linear;
}

.slider-thumb.dragging {
  cursor: grabbing;
}

.slider-thumb-btn {
  border: 0;
  cursor: grab;
  padding: 0;
}

.code-row {
  display: grid;
  grid-template-columns: 1fr 120px;
  gap: 10px;
  align-items: center;
}

.code-btn {
  height: 44px;
  border-radius: 14px;
  border: 0;
  background: #f1f5f9;
  color: #4f46e5;
  font-weight: 800;
  font-size: 13px;
  cursor: pointer;
  transition: background 0.2s ease;
}

.code-btn:hover {
  background: #eef2ff;
}

.code-btn:disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

.btn {
  width: 100%;
  height: 46px;
  border-radius: 14px;
  border: 0;
  cursor: pointer;
  font-weight: 800;
  font-size: 15px;
}

.btn-primary {
  color: #ffffff;
  background: linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%);
  box-shadow: 0 4px 20px -5px rgba(79, 70, 229, 0.4);
  transition: transform 0.25s cubic-bezier(0.23, 1, 0.32, 1),
    box-shadow 0.25s cubic-bezier(0.23, 1, 0.32, 1),
    filter 0.25s cubic-bezier(0.23, 1, 0.32, 1);
}

.btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 30px -5px rgba(79, 70, 229, 0.5);
  filter: brightness(1.03);
}

.btn:disabled {
  opacity: 0.75;
  cursor: not-allowed;
  transform: none;
}

.row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 13px;
  margin-top: 2px;
  margin-bottom: 2px;
}

.remember {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #475569;
  cursor: pointer;
}

.remember input {
  width: 14px;
  height: 14px;
}

.link {
  border: 0;
  background: transparent;
  color: #4f46e5;
  cursor: pointer;
  font-weight: 700;
  padding: 0;
}

.link:hover {
  color: #4338ca;
}

.link.strong {
  font-weight: 900;
}

.divider {
  height: 1px;
  background: #f1f5f9;
  margin: 16px 0 12px;
}

.register {
  display: flex;
  justify-content: center;
  gap: 6px;
  color: #64748b;
  font-size: 13px;
}

.loading {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 46px;
}

.spinner {
  width: 18px;
  height: 18px;
  animation: spin 1s linear infinite;
}

.spinner-track {
  fill: none;
  stroke: rgba(255, 255, 255, 0.35);
  stroke-width: 4;
}

.spinner-head {
  fill: none;
  stroke: #ffffff;
  stroke-width: 4;
  stroke-linecap: round;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.title-reveal {
  animation: fadeUp 0.8s ease-out forwards;
  opacity: 0;
  transform: translateY(20px);
}

.delay-100 {
  animation-delay: 0.1s;
}

.delay-200 {
  animation-delay: 0.2s;
}

.delay-300 {
  animation-delay: 0.3s;
}

@keyframes fadeUp {
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.footer-copyright {
  position: relative;
  z-index: 1;
  text-align: center;
  padding: 20px 12px 28px;
  color: #94a3b8;
  font-size: 12px;
  line-height: 1.8;
}
</style>
